import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  headerLeft?: React.ReactNode;
  headerCenter?: React.ReactNode;
  headerRight?: React.ReactNode;
  title?: React.ReactNode; // for backward compatibility, used as headerCenter if headerCenter not provided
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  className?: string; // <-- add this line
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  headerLeft,
  headerCenter,
  headerRight,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  className
}) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 } as any}
            animate={{ opacity: 1 } as any}
            exit={{ opacity: 0 } as any}
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 } as any}
              animate={{ opacity: 1, scale: 1, y: 0 } as any}
              exit={{ opacity: 0, scale: 0.95, y: 20 } as any}
              transition={{ type: 'spring', duration: 0.3 }}
              className={`relative w-full rounded-lg shadow-xl font-[var(--font-family)] text-[var(--color-text)] bg-[var(--color-surface)] border border-[var(--color-border)] flex flex-col ${className?.includes('max-w-') ? '' : sizes[size]} ${className?.includes('max-h-') ? '' : 'max-h-[90vh]'} ${className || ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              {(headerLeft || headerCenter || headerRight || showCloseButton || title) && (
                <div className="flex items-center p-6 border-b border-[var(--color-border)] relative">
                  {/* Left: Close Button or headerLeft */}
                  <div className="flex items-center justify-start min-w-[48px]">
                    {showCloseButton && (
                      <button
                        onClick={onClose}
                        className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-background)] rounded-lg transition-colors font-[var(--font-family)]"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                    {headerLeft}
                  </div>
                  {/* Center: headerCenter or title */}
                  <div className="flex-1 flex justify-center">
                    {(headerCenter || title) && (
                      <h2 className="text-lg font-semibold font-[var(--font-family)]">
                        {headerCenter || title}
                      </h2>
                    )}
                  </div>
                  {/* Right: headerRight */}
                  <div className="flex items-center justify-end min-w-[200px]">
                    {headerRight}
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="p-6 font-[var(--font-family)] overflow-y-auto">
                {children}
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Modal; 